import { CustomTooltip, TooltipFormatter } from '@/shared/ui/components';
import { ViewIcon2, mesageIcon } from '@/shared/ui/assets';
import { DOUBLE_HYPHEN_BULLET, SUPPORT_REQUEST_STATUS } from '@/shared/config';
import { SUPPORT_REQUEST_TYPE } from '@/entities/support';
import { checkShowCommentIcon, zeroPad, generateFullName } from '@/shared/lib';
import classNames from 'classnames';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';

/**
 * Renders the support request ID cell together with an unread-comment marker. Zero-pads the ticket id to five digits and appends a tooltipped message icon when checkShowCommentIcon on lastSeenTicketComment.createdBy.id reports an unseen comment, showing a Skeleton while isLoadedData is false.
 *
 * @param cell - Raw value of the column for this row.
 * @param row - Full support request record backing the row.
 * @param rowIndex - Zero-based index of the row.
 * @param isLoadedData - Whether the table data has finished loading.
 * @returns The padded request id with an optional comment icon, or a loading skeleton.
 */
export const requestIdFormatter = (cell: any, row: any, rowIndex: any, isLoadedData: any) => {
  const { lastSeenTicketComment } = row;
  const hasComment = checkShowCommentIcon(lastSeenTicketComment?.createdBy?.id);
  return isLoadedData ? (
    <div className="d-flex">
      <span>{zeroPad(cell, 5)}</span>
      {hasComment && (
        <CustomTooltip title={t`New comment(s) received`} placement="bottom">
          <img
            src={mesageIcon}
            alt="Message Icon"
            style={{ cursor: 'pointer', marginLeft: '8px' }}
          />
        </CustomTooltip>
      )}
    </div>
  ) : (
    <Skeleton count={1} />
  );
};

/**
 * Renders the support request status cell as a colored status label. Maps the cell value through SUPPORT_REQUEST_STATUS and derives a lowercased class modifier for the label, falling back to DOUBLE_HYPHEN_BULLET for an unmapped status and to a Skeleton while isLoadedData is false.
 *
 * @param cell - Raw value of the column for this row.
 * @param row - Full support request record backing the row.
 * @param rowIndex - Zero-based index of the row.
 * @param isLoadedData - Whether the table data has finished loading.
 * @returns The status label element, or a loading skeleton.
 */
export const requestStatusFormatter = (cell: any, row: any, rowIndex: any, isLoadedData: any) => {
  if (isLoadedData) {
    const statusFormat = SUPPORT_REQUEST_STATUS[cell];
    const prefixClass = statusFormat ? `--${_.lowerCase(statusFormat)}` : '';
    return (
      <div className="d-flex">
        {statusFormat ? (
          <div className={classNames('request-status-label', prefixClass)}>{statusFormat}</div>
        ) : (
          DOUBLE_HYPHEN_BULLET
        )}
      </div>
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the name of the user who raised the support request. Builds the display name from row.requestedBy through generateFullName inside a span, or shows a single-line Skeleton while isLoadedData is false.
 *
 * @param cell - Raw value of the column for this row.
 * @param row - Full support request record backing the row.
 * @param rowIndex - Zero-based index of the row.
 * @param isLoadedData - Whether the table data has finished loading.
 * @returns The requestor name element, or a loading skeleton.
 */
export const requestorFormatter = (cell: any, row: any, rowIndex: any, isLoadedData: any) => {
  const { requestedBy } = row;
  if (isLoadedData) {
    return <span>{generateFullName(requestedBy)}</span>;
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the requestor role list of a support request row. Joins the cell array with commas into a single-row TooltipFormatter, or shows a single-line Skeleton while isLoadedData is false.
 *
 * @param cell - Raw value of the column for this row.
 * @param row - Full support request record backing the row.
 * @param rowIndex - Zero-based index of the row.
 * @param isLoadedData - Whether the table data has finished loading.
 * @returns The truncated role list element, or a loading skeleton.
 */
export const rolesFormatter = (cell: any, row: any, rowIndex: any, isLoadedData: any) => {
  if (isLoadedData) {
    return (
      <TooltipFormatter className="requestor-role-tooltip" text={_.join(cell, ', ')} rows={1} />
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the support request category cell as its readable label. Looks the cell value up in SUPPORT_REQUEST_TYPE and wraps it in a span, falling back to a single-line Skeleton while isLoadedData is false.
 *
 * @param cell - Raw value of the column for this row.
 * @param row - Full support request record backing the row.
 * @param rowIndex - Zero-based index of the row.
 * @param isLoadedData - Whether the table data has finished loading.
 * @returns The category label element, or a loading skeleton.
 */
export const categoryFormatter = (cell: any, row: any, rowIndex: any, isLoadedData: any) => {
  if (isLoadedData) {
    const categoryFormat = SUPPORT_REQUEST_TYPE[cell];
    return <span>{categoryFormat || DOUBLE_HYPHEN_BULLET}</span>;
  }
  return <Skeleton count={1} />;
};

/**
 * Renders a plain support request list cell value. Wraps the cell in a span once isLoadedData is true and shows a single-line Skeleton otherwise.
 *
 * @param cell - Raw value of the column for this row.
 * @param row - Full support request record backing the row.
 * @param rowIndex - Zero-based index of the row.
 * @param isLoadedData - Whether the table data has finished loading.
 * @returns The cell value element, or a loading skeleton.
 */
export const cellFormatter = (cell: any, row: any, rowIndex: any, isLoadedData: any) =>
  isLoadedData ? <span>{cell}</span> : <Skeleton count={1} />;

/**
 * Renders the row action cell of the support request list as a tooltipped view icon. Returns undefined until isLoadedData is true, otherwise an image whose click passes row.id to handleClickViewRequestDetail.
 *
 * @param cell - Raw value of the column for this row.
 * @param row - Full support request record backing the row.
 * @param rowIndex - Zero-based index of the row.
 * @param isLoadedData - Whether the table data has finished loading.
 * @param handleClickViewRequestDetail - Callback opening the detail view for a request id.
 * @returns The action icon element, or undefined while the row is still loading.
 */
export const actionIconFormatter = (
  cell: any,
  row: any,
  rowIndex: any,
  isLoadedData: any,
  handleClickViewRequestDetail: any,
) => {
  if (isLoadedData) {
    return (
      <div className="action">
        <CustomTooltip title={t`View request`} placement="bottom">
          <img
            src={ViewIcon2}
            alt="View Icon"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              handleClickViewRequestDetail(row.id);
            }}
          />
        </CustomTooltip>
      </div>
    );
  }
  return undefined;
};
